import { AxiosPromise } from 'axios';
import { instance } from '.';
import { URLS } from './index';
import { Logger } from 'fsts';
import invoicePositionFunctions, {
  InvoicePosition,
  Value,
} from '../model/invoicePosition';
import { SearchParams } from '../model/searchParams';
import ODataFilterBuilder from 'odata-filter-builder';
import { CONST } from '../utils/constants';
import { DefaultBackendHelper } from '../utils/backendHelper';
import { InvoicePaymentStatus, getPaymentStatusText } from '../model/invoice';
import attendeeFixedCostFuncs, {
  AttendeeFixedCost,
} from '../model/attendeeFixedCost';
import attendeeFuncs, { Attendee } from '../model/attendee';
import referenceParentFuncs, {
  ReferenceParent,
} from '../model/referenceParent';

const logger = new Logger('backend.InvoicePositions');

export interface BackendInvoicePosition {
  getInvoicePositions(
    searchParams: SearchParams,
    invoiceId?: string,
    attendeeAppUserId?: string,
    filterByStatuses?: InvoicePaymentStatus[],
    locationId?: string,
    departmentId?: string,
    dateFrom?: string,
    dateTill?: string
  ): AxiosPromise<Value>;
  getInvoicePositionsForActivatedAttendee(
    attendee: Attendee,
    attendeeFixedCosts?: AttendeeFixedCost[],
    departmentIds?: string[],
    lang?: string
  ): AxiosPromise<InvoicePosition[]>;
  getInvoicePositionsByMembershipTypeSubscriptions(
    attendeeAppUserId: string,
    membershipTypeAttendees: ReferenceParent[],
    lang?: string
  ): AxiosPromise<InvoicePosition[]>;
  addInvoicePosition(
    invoicePosition: InvoicePosition,
    culture: string
  ): AxiosPromise<any>;
  addInvoicePositionsByCosts(
    attendeeAppUserId: string,
    costEntries: {
      costId: string;
      forAttendeeId?: string;
      departmentId?: string;
    }[],
    customCostEntries: {
      euroCost: number;
      description: string;
      forAttendeeId?: string;
      departmentId?: string;
    }[],
    singleInvoice: boolean,
    paymentStatus: InvoicePaymentStatus,
    culture: string
  ): AxiosPromise<any>;
  addInvoicePositionsForActivatedAttendee(
    attendeeId: string,
    membershipTypeAttendees: ReferenceParent[],
    attendeeFixedCosts: AttendeeFixedCost[],
    departmentIds: string[],
    lang?: string
  ): AxiosPromise<any>;
  cancelInvoicePosition(invoicePositionId: string): AxiosPromise<any>;
  unCancelInvoicePosition(invoicePositionId: string): AxiosPromise<any>;
  updateInvoicePosition(invoicePosition: InvoicePosition): AxiosPromise<any>;
  exportInvoicePositions(
    filterByMembershipTypeIds: string[],
    filterByCourseIds: string[],
    filterByCostIds: string[],
    filterByAttendeeIds: string[],
    filterByPaymentStatuses: InvoicePaymentStatus[],
    fromDate: string | undefined,
    tillDate: string | undefined,
    culture: string
  ): AxiosPromise<any>;
}

export const DefaultBackendInvoicePosition: BackendInvoicePosition = {
  getInvoicePositions(
    searchParams: SearchParams,
    invoiceId?: string,
    attendeeAppUserId?: string,
    filterByStatuses?: InvoicePaymentStatus[],
    locationId?: string,
    departmentId?: string,
    dateFrom?: string,
    dateTill?: string
  ): AxiosPromise<Value> {
    var odfb = ODataFilterBuilder('and');

    if (invoiceId != undefined) {
      odfb.eq(CONST.InvoiceId, invoiceId, false);
    }

    if (attendeeAppUserId != undefined) {
      odfb.eq(CONST.AttendeeAppUserId, attendeeAppUserId, false);
    }

    if (filterByStatuses != undefined && filterByStatuses.length > 0) {
      let odfbStati = ODataFilterBuilder('or');
      filterByStatuses.forEach((x) =>
        odfbStati.eq(
          CONST.InvoicePositionPaymentStatus,
          getPaymentStatusText(x)
        )
      );
      odfb.and(odfbStati);
    }

    if (departmentId != undefined) {
      odfb.eq(CONST.DepartmentId, departmentId, false);
    }

    if (dateFrom !== undefined) {
      odfb.ge(CONST.CreatedDate, dateFrom, false);
    }

    if (dateTill !== undefined) {
      odfb.le(CONST.CreatedDate, dateTill, false);
    }

    let baseUrl =
      locationId != undefined
        ? `${URLS.invoicePositionOdata}/GetInLocation(locationId=${locationId})`
        : // : departmentId != undefined
          // ? `${URLS.invoicePositionOdata}/GetInDepartment(departmentId=${departmentId})`
          URLS.invoicePositionOdata;

    let url = DefaultBackendHelper.makeUrl(
      baseUrl,
      searchParams.dataOption,
      searchParams.orClauseFieldsIds,
      searchParams.filter,
      odfb
    );

    logger.log(`getInvoicePositions${url}`);
    return instance.get<Value>(url);
  },
  getInvoicePositionsForActivatedAttendee(
    attendee: Attendee,
    attendeeFixedCosts?: AttendeeFixedCost[],
    departmentIds?: string[],
    lang?: string
  ): AxiosPromise<InvoicePosition[]> {
    const payload = attendeeFuncs.toAPI(attendee);
    payload.AttendeeFixedCosts =
      attendeeFixedCosts?.map(attendeeFixedCostFuncs.toAPI) ?? null;
    payload.DepartmentIds = departmentIds ?? null;

    return instance.post<InvoicePosition[]>(
      `${URLS.invoicePosition}/GetForActivatedAttendee`,
      payload,
      { params: { lang: lang } }
    );
  },
  getInvoicePositionsByMembershipTypeSubscriptions(
    attendeeAppUserId: string,
    membershipTypeSubscriptions: ReferenceParent[],
    lang?: string
  ): AxiosPromise<InvoicePosition[]> {
    const payload = {
      AttendeeAppUserId: attendeeAppUserId,
      ReferenceParents: membershipTypeSubscriptions.map(
        referenceParentFuncs.toAPI
      ),
    };

    return instance.post<InvoicePosition[]>(
      `${URLS.invoicePosition}/GetByMembershipTypeSubscriptions`,
      payload,
      { params: { lang: lang } }
    );
  },
  addInvoicePosition(
    invoicePosition: InvoicePosition,
    culture: string
  ): AxiosPromise<any> {
    return instance.post<InvoicePosition>(
      URLS.invoicePosition,
      invoicePositionFunctions.toAPI(invoicePosition),
      { params: { culture: culture } }
    );
  },
  addInvoicePositionsByCosts(
    attendeeAppUserId: string,
    costEntries: {
      costId: string;
      forAttendeeId?: string;
      departmentId?: string;
    }[],
    customCostEntries: {
      euroCost: number;
      description: string;
      forAttendeeId?: string;
      departmentId?: string;
      customCostInfo?: string;
    }[],
    singleInvoice: boolean = false,
    paymentStatus: InvoicePaymentStatus = InvoicePaymentStatus.Open,
    culture: string
  ): AxiosPromise<any> {
    return instance.post(
      `${URLS.invoicePosition}/AddByCosts`,
      {
        AttendeeAppUserId: attendeeAppUserId,
        CostEntries: costEntries,
        CustomCostEntries: customCostEntries,
        SingleInvoice: singleInvoice,
        PaymentStatus: paymentStatus,
      },
      {
        params: { culture: culture },
      }
    );
  },
  addInvoicePositionsForActivatedAttendee(
    attendeeId: string,
    membershipTypeAttendees: ReferenceParent[],
    attendeeFixedCosts: AttendeeFixedCost[],
    departmentIds: string[],
    lang?: string
  ): AxiosPromise<any> {
    const payload = {
      AttendeeId: attendeeId,
      MembershipTypeAttendees: membershipTypeAttendees.map(
        referenceParentFuncs.toAPI
      ),
      AttendeeFixedCosts: attendeeFixedCosts.map(attendeeFixedCostFuncs.toAPI),
      DepartmentIds: departmentIds,
    };

    return instance.post(
      `${URLS.invoicePosition}/AddForActivatedAttendee`,
      payload,
      { params: { lang: lang } }
    );
  },
  cancelInvoicePosition(invoicePositionId: string): AxiosPromise<any> {
    return instance.post(`${URLS.invoicePosition}/Cancel/${invoicePositionId}`);
  },
  unCancelInvoicePosition(invoicePositionId: string): AxiosPromise<any> {
    return instance.post(
      `${URLS.invoicePosition}/UnCancel/${invoicePositionId}`
    );
  },
  updateInvoicePosition(invoicePosition: InvoicePosition): AxiosPromise<any> {
    return instance.put(
      URLS.invoicePosition,
      invoicePositionFunctions.toAPI(invoicePosition)
    );
  },
  exportInvoicePositions(
    filterByMembershipTypeIds: string[],
    filterByCourseIds: string[],
    filterByCostIds: string[],
    filterByAttendeeIds: string[],
    filterByPaymentStatuses: InvoicePaymentStatus[],
    fromDate: string | undefined,
    tillDate: string | undefined,
    culture: string
  ): AxiosPromise<any> {
    let url = `${URLS.invoicePosition}/ExportInvoicePositions`;
    url = DefaultBackendHelper.addParamsToUrl(url, [`culture=${culture}`]);
    return instance.post(
      url,
      {
        FilterByMembershipTypeIds: filterByMembershipTypeIds,
        FilterByCourseIds: filterByCourseIds,
        FilterByCostIds: filterByCostIds,
        FilterByAttendeeIds: filterByAttendeeIds,
        FilterByPaymentStatuses: filterByPaymentStatuses,
        FromDate: fromDate,
        TillDate: tillDate,
      },
      { responseType: 'blob' }
    );
  },
};
